import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Check, Zap, Crown, Calendar, ShieldCheck, Loader2, IndianRupee,
} from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const PLAN_META = {
  basic: { icon: Zap, tone: "text-primary" },
  pro: { icon: Crown, tone: "text-brand-green" },
};

export default function Subscription() {
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [p, m] = await Promise.all([
      api.get("/subscriptions/plans"),
      api.get("/subscriptions/me"),
    ]);
    setPlans(p.data.plans);
    setMe(m.data);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (plan) => {
    setLoading(true);
    try {
      const { data: order } = await api.post("/subscriptions/create-order", { plan: plan.key });
      const finish = async (payment_id, signature) => {
        await api.post("/subscriptions/verify", {
          subscription_id: order.subscription_id,
          payment_order_id: order.order_id,
          payment_id, signature,
        });
        toast.success(`${plan.name} activated for 30 days`);
        load();
      };
      if (order.provider === "mock") {
        await finish(`mock_pay_${Date.now()}`, "mock");
      } else {
        const ok = await loadRazorpay();
        if (!ok) return toast.error("Razorpay failed to load");
        new window.Razorpay({
          key: order.key_id, amount: order.amount_paise, currency: order.currency,
          order_id: order.order_id, name: "MaaPrint",
          description: `${plan.name} subscription (30 days)`,
          theme: { color: "#0F62FE" },
          handler: async (res) => finish(res.razorpay_payment_id, res.razorpay_signature),
        }).open();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!me) return null;
  const active = me.active;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="subscription-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Membership</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Subscription</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Keep your MaaPrint shop live for walk-in customers. Renew every 30 days.
        </p>
      </div>

      {active ? (
        <Card className="p-6 border-brand-green/30 bg-brand-green/5" data-testid="sub-active-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-brand-green" size={20} />
                <span className="font-display font-semibold text-lg">{active.plan.toUpperCase()} — Active</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                <Calendar size={14} /> Expires {new Date(active.expires_at).toLocaleDateString()}
              </p>
            </div>
            <Badge className="bg-brand-green text-white">Active</Badge>
          </div>
        </Card>
      ) : (
        <Card className="p-6 border-amber-500/40 bg-amber-500/5">
          <p className="text-sm">
            <b>Your shop is offline for walk-ins.</b> Activate a subscription below to start accepting customer QR uploads.
          </p>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((plan) => {
          const meta = PLAN_META[plan.key] || {};
          const Icon = meta.icon || Zap;
          const isCurrent = active?.plan === plan.key;
          return (
            <Card key={plan.key} className="p-6 border-border bg-card relative overflow-hidden" data-testid={`plan-${plan.key}`}>
              <div className="flex items-center gap-2">
                <Icon className={meta.tone || "text-primary"} size={22} />
                <h3 className="font-display font-semibold text-xl">{plan.name}</h3>
                {plan.key === "pro" && <Badge className="bg-brand-green text-white ml-auto">Popular</Badge>}
              </div>
              <p className="mt-3">
                <span className="text-4xl font-display font-bold tabular-nums">₹{plan.price_inr}</span>
                <span className="text-sm text-muted-foreground ml-1">/ 30 days</span>
              </p>
              <ul className="mt-6 space-y-2">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check size={16} className="text-brand-green flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => subscribe(plan)}
                disabled={loading || isCurrent}
                className={`mt-6 w-full h-11 ${plan.key === "pro" ? "bg-brand-green hover:bg-brand-green-hover" : "bg-primary hover:bg-brand-blue-hover"}`}
                data-testid={`subscribe-${plan.key}-btn`}
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <IndianRupee size={14} className="mr-1" />}
                {isCurrent ? "Current plan" : `Subscribe · ₹${plan.price_inr}`}
              </Button>
            </Card>
          );
        })}
      </div>

      {me.history?.length > 0 && (
        <Card className="p-6 border-border bg-card">
          <h3 className="font-display font-semibold mb-4">Billing history</h3>
          <div className="divide-y divide-border">
            {me.history.map((h) => (
              <div key={h.id} className="py-3 flex items-center justify-between text-sm" data-testid={`sub-hist-${h.id}`}>
                <div>
                  <p className="font-medium capitalize">{h.plan} plan</p>
                  <p className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium tabular-nums">₹{h.amount}</p>
                  <Badge variant="outline" className={
                    h.status === "active" ? "bg-brand-green/10 text-brand-green border-brand-green/20" :
                    h.status === "pending" ? "bg-amber-500/10 text-amber-600 border-amber-500/20" :
                    "bg-muted"
                  }>{h.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
